import type { ReactNode } from 'react';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import { Card, Tag } from '../../merchant/ui/primitives.js';
import { ReferenceMetrics } from './RecommendationReferences.js';
import { ReferenceDetails } from './ReferenceDetails.js';
import {
  instagramReferenceUrl,
  referenceGroups,
  researchUi,
  type ReferenceIdea,
} from './researchPresentation.js';

/** One exact-snapshot list shared by Research and Idea, without new reads. */
export function ResearchReferenceList({
  ideas,
  copy,
  language,
}: {
  readonly ideas: readonly ReferenceIdea[];
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
}): ReactNode {
  const ui = researchUi[language];
  const references = referenceGroups(ideas);
  return (
    <section className="mj-ws-mt-20" aria-label={ui.references}>
      <h3 className="mj-ws-h3">
        {ui.references} · {references.length}
      </h3>
      {references.length === 0 ? (
        <p className="mj-ws-muted mj-ws-sm">
          {ideas.some((idea) => idea.basis === 'TOPIC_BASED')
            ? language === 'ar'
              ? 'هذه الأفكار مبنية على موضوعك؛ لا تتوفر مراجع مؤكدة لهذه الجولة.'
              : 'These ideas are based on your topic; no confirmed references are available for this run.'
            : ideas.length > 0 && ideas.every((idea) => idea.basis === 'BUSINESS_BASED')
              ? copy.line.noReferences
              : copy.line.referencesUnavailable}
        </p>
      ) : null}
      <div className="mj-ws-evidence-list mj-ws-mt-12">
        {references.map(({ key, reference, usedBy }) => (
          <Card key={key} as="article">
            <div className="mj-ws-row">
              <a
                href={instagramReferenceUrl(reference) ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                {reference.creatorHandle === null
                  ? reference.platform
                  : `@${reference.creatorHandle} · ${reference.platform}`}
              </a>
              {reference.origin === null ? null : (
                <Tag>
                  {reference.origin === 'SAVED' ? copy.line.referenceSaved : ui.live}
                </Tag>
              )}
              {reference.category === null ? null : (
                <Tag>
                  {reference.category === 'DIRECT'
                    ? copy.line.referenceDirect
                    : copy.line.referenceRelated}
                </Tag>
              )}
            </div>
            <p className="mj-ws-sm">
              <ReferenceMetrics reference={reference} copy={copy} language={language} />
            </p>
            <p className="mj-ws-xs">
              {ui.usedBy}:{' '}
              <span dir="auto">{usedBy.map((idea) => idea.title).join(' · ')}</span>
            </p>
            <ReferenceDetails reference={reference} language={language} />
          </Card>
        ))}
      </div>
    </section>
  );
}
